import { apiClient } from './client'
import type {
  DeliveryView,
  NotificationPreferenceView,
  NotificationView,
  UnreadCountView,
  UpsertNotificationPreferenceRequest,
} from '../types/notification'

/**
 * Typed fetch wrappers for `module.notify`'s in-app Notification center + own-row preferences
 * (`NotificationController`/`NotificationPreferenceController`) — same thin-wrapper convention as
 * `api/tasks.ts`. Backs `screens/notifications/NotificationsScreen.tsx`.
 *
 * Every route here is own-principal implicit (`TenantContext.actorId()` server-side) — there is no
 * caller-supplied principal id anywhere in this file, matching the backend's own posture
 * (docs/api-spec.md §23).
 */

/** `GET /notifications` — the caller's own notification center feed, newest first. */
export async function listNotifications(signal?: AbortSignal): Promise<NotificationView[]> {
  const { data } = await apiClient.get<NotificationView[]>('/notifications', { signal })
  return data
}

/** `GET /notifications/unread-count` — backs the NavShell bell badge. */
export async function getUnreadCount(signal?: AbortSignal): Promise<UnreadCountView> {
  const { data } = await apiClient.get<UnreadCountView>('/notifications/unread-count', { signal })
  return data
}

/** `POST /notifications/{id}/read` — marks exactly one notification read. */
export async function markNotificationRead(id: string): Promise<void> {
  await apiClient.post<void>(`/notifications/${id}/read`, {})
}

/** `POST /notifications/mark-all-read` — one call, not N round-trips. */
export async function markAllNotificationsRead(): Promise<{ markedRead: number }> {
  const { data } = await apiClient.post<{ markedRead: number }>('/notifications/mark-all-read', {})
  return data
}

/** `GET /notifications/{id}/deliveries` — thin additive GET (IB-166); every channel this ONE
 * notification fanned out to for the caller. */
export async function listNotificationDeliveries(id: string, signal?: AbortSignal): Promise<DeliveryView[]> {
  const { data } = await apiClient.get<DeliveryView[]>(`/notifications/${id}/deliveries`, { signal })
  return data
}

/** `GET /notification-preferences` — the caller's own explicit overrides only (an unlisted
 * category×channel pair simply inherits the tenant default — `PreferenceGate`'s own javadoc). */
export async function listNotificationPreferences(
  signal?: AbortSignal,
): Promise<NotificationPreferenceView[]> {
  const { data } = await apiClient.get<NotificationPreferenceView[]>('/notification-preferences', { signal })
  return data
}

/** `PUT /notification-preferences` — creates the explicit override on first write, or updates it. */
export async function upsertNotificationPreference(
  request: UpsertNotificationPreferenceRequest,
): Promise<NotificationPreferenceView> {
  const { data } = await apiClient.put<NotificationPreferenceView>('/notification-preferences', request)
  return data
}
